import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ProfileEditDialog({ open, onOpenChange }) {
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", cep: "", street: "", number: "", neighborhood: "", city: "", state: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!open || !user) return;
    const a = user.address || {};
    setForm({
      name: user.name || "", phone: user.phone || "",
      cep: a.cep || "", street: a.street || "", number: a.number || "",
      neighborhood: a.neighborhood || "", city: a.city || "", state: a.state || "",
    });
  }, [open, user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/me/profile", {
        name: form.name, phone: form.phone,
        address: { cep: form.cep, street: form.street, number: form.number, neighborhood: form.neighborhood, city: form.city, state: form.state },
      });
      await checkAuth();
      toast.success("Dados atualizados!");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="profile-edit-dialog">
        <DialogHeader><DialogTitle>Meus dados</DialogTitle>
          <DialogDescription>Atualize seu nome, telefone e endereço.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5"><Label>Nome</Label>
            <Input required value={form.name} onChange={upd("name")} data-testid="profile-name-input" /></div>
          <div className="space-y-1.5"><Label>Telefone</Label>
            <Input value={form.phone} onChange={upd("phone")} placeholder="(21) 99999-0000" data-testid="profile-phone-input" /></div>
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <p className="text-xs font-semibold text-slate-500">Endereço</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label className="text-xs">CEP</Label>
                <Input value={form.cep} onChange={upd("cep")} data-testid="profile-cep-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Número</Label>
                <Input value={form.number} onChange={upd("number")} data-testid="profile-number-input" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs">Rua</Label>
                <Input value={form.street} onChange={upd("street")} data-testid="profile-street-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Bairro</Label>
                <Input value={form.neighborhood} onChange={upd("neighborhood")} data-testid="profile-neighborhood-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Cidade</Label>
                <Input value={form.city} onChange={upd("city")} data-testid="profile-city-input" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs">Estado (UF)</Label>
                <Input value={form.state} onChange={upd("state")} maxLength={2} data-testid="profile-state-input" /></div>
            </div>
          </div>
          <p className="text-xs text-slate-400">E-mail e CPF/CNPJ não podem ser alterados por aqui. Fale com o suporte se precisar.</p>
          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="profile-save-button">
            {busy ? "Salvando..." : "Salvar alterações"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
